import React, { FC, useRef, useEffect } from "react";
import { Button, ThemeProps } from "common/components/ui";
import { FontWeight } from "common/components/ui/shared/theme/font";
import { FormattedMessage } from "common/components/ui/FormattedMessage";
import styled from "@emotion/styled";
import { StyledText, StyledInfoIcon } from "../sharedComponents";
import { CustomCollapsibleSection } from "./CustomCollapsibleSection";

const FreightDeliveryContainer = styled.div<ThemeProps>(
  ({ theme }) => `
    padding: 0 ${theme.spacing.S7};
  `
);

const FreightInfoGrid = styled.div<ThemeProps>(
  ({ theme }) => `
    margin-top: ${theme.spacing.S4};
    display: grid;
    grid-template-columns: 1.5fr 1fr 1fr;
  `
);

const FreightInfoColumn = styled.div<ThemeProps>(
  ({ theme }) => `
    padding: ${theme.spacing.S5} ${theme.spacing.S7};
    
    &:first-of-type {
      padding-bottom: ${theme.spacing.S7};
    }
  `
);

const FreightInfoLabel = styled(StyledText)<ThemeProps>(
  ({ theme }) => `
    color: ${theme.colors.NEUTRAL[400]};
    margin-bottom: ${theme.spacing.S2};
    font-size: ${theme.font.size.F2};
    font-weight: ${theme.font.weight[FontWeight.BOLD]};
  `
);

const FreightInfoValue = styled(StyledText)<ThemeProps>(
  ({ theme }) => `
    font-size: ${theme.font.size.F3};
    margin-top: ${theme.spacing.S1};
    font-weight: ${theme.font.weight[FontWeight.REGULAR]};
  `
);

const StyledButton = styled(Button)<ThemeProps>(
  ({ theme }) => `
    margin-top: ${theme.spacing.S4};
    background: ${theme.colors.NEUTRAL["00"]};
    color: ${theme.colors.BLUE[300]};
    border: ${theme.border.width.B1} ${theme.border.type} ${theme.colors.BLUE[300]};
    padding: ${theme.spacing.S2} ${theme.spacing.S4};
    font-weight: ${theme.font.weight[FontWeight.BOLD]};
    min-width: 120px;
    white-space: nowrap;
    
    &:hover {
      background: ${theme.colors.BLUE[40]};
    }
  `
);

const TitleContainer = styled.div<ThemeProps>(
  ({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacing.S1};
    padding: ${theme.spacing.S2} ${theme.spacing.S7} 0;
  `
);

const TitleText = styled(StyledText)<ThemeProps>(
  ({ theme }) => `
    font-weight: ${theme.font.weight[FontWeight.BOLD]};
  `
);

interface CommonFreightDeliveryInformationProps {
  onDownloadBol?: () => void;
  isDownloadLoading?: boolean;
  isDefaultOpen?: boolean;
  proNumber?: string;
  bolId?: string;
  containerId?: string;
  checkedOutAt?: string;
  attachments?: Array<{ url: string; attachmentType: string | null }>;
}

export const CommonFreightDeliveryInformation: FC<CommonFreightDeliveryInformationProps> = ({
  onDownloadBol,
  isDownloadLoading = false,
  isDefaultOpen = true,
  proNumber,
  bolId,
  containerId,
  checkedOutAt,
  attachments = [],
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const finalUpdatedAt = checkedOutAt;

  // Check if any of the important identifiers are present
  const hasAnyIdentifier = Boolean(proNumber ?? bolId ?? containerId);
  const hasDownloadFunction = typeof onDownloadBol === "function";

  // Check if there are any attachments with URLs available for download
  const hasAttachments = Array.isArray(attachments) && attachments.some((attachment) => Boolean(attachment?.url));

  const shouldRenderComponent = hasAnyIdentifier || (hasDownloadFunction && hasAttachments);
  // Show download button if we have the download function AND attachments are available
  const shouldShowDownloadButton = hasDownloadFunction && hasAttachments;

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const container = containerRef.current;
      if (container) {
        container.querySelectorAll("table").forEach((table) => {
          table.style.tableLayout = "auto";
          table.style.width = "100%";
        });
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, []);

  // If no identifiers or download function are present, don't render anything
  if (!shouldRenderComponent) {
    return null;
  }

  const content = (
    <FreightDeliveryContainer>
      <TitleContainer>
        <TitleText size="bodyLarge">
          <FormattedMessage
            id="inbounds.LtlExternalConfirmation.freightDeliveryInfo"
            defaultMessage="Freight Delivery Information"
          />
        </TitleText>
        <StyledInfoIcon
          message={
            <FormattedMessage
              id="inbounds.LtlExternalConfirmation.freightDeliveryInfoTooltip"
              defaultMessage="Freight delivery information is provided by Flexport"
            />
          }
        />
      </TitleContainer>
      <FreightInfoGrid>
        <FreightInfoColumn>
          <FreightInfoLabel size="body">
            <FormattedMessage
              id="inbounds.LtlExternalConfirmation.deliveryAcceptance"
              defaultMessage="Delivery Acceptance"
            />
          </FreightInfoLabel>
          <FreightInfoValue size="bodyLarge">{finalUpdatedAt ?? "-"}</FreightInfoValue>
          {shouldShowDownloadButton && onDownloadBol && (
            <StyledButton
              appearance="DEFAULT"
              onClick={onDownloadBol}
              loading={isDownloadLoading}
              disabled={isDownloadLoading}
            >
              <FormattedMessage id="inbounds.LtlExternalConfirmation.downloadBol" defaultMessage="Download BOL" />
            </StyledButton>
          )}
        </FreightInfoColumn>
        <FreightInfoColumn>
          <FreightInfoLabel size="body">
            <FormattedMessage id="inbounds.LtlExternalConfirmation.proNumber" defaultMessage="PRO" />
          </FreightInfoLabel>
          <FreightInfoValue size="bodyLarge">{proNumber ?? "-"}</FreightInfoValue>
        </FreightInfoColumn>
        <FreightInfoColumn>
          <FreightInfoLabel size="body">
            <FormattedMessage id="inbounds.LtlExternalConfirmation.containerId" defaultMessage="Container ID" />
          </FreightInfoLabel>
          <FreightInfoValue size="bodyLarge">{containerId ?? "-"}</FreightInfoValue>
        </FreightInfoColumn>
      </FreightInfoGrid>
    </FreightDeliveryContainer>
  );

  return (
    <div ref={containerRef} data-testid="freight-delivery-container">
      <CustomCollapsibleSection
        isDefaultOpen={isDefaultOpen}
        isBolded
        noFill
        expandedText={
          <StyledText size="body" color="BLUE.300" bold>
            <FormattedMessage
              id="inbounds.LtlExternalConfirmation.hideShipmentDetails"
              defaultMessage="Hide shipment details"
            />
          </StyledText>
        }
        collapsedText={
          <StyledText size="body" color="BLUE.300" bold>
            <FormattedMessage
              id="inbounds.LtlExternalConfirmation.showShipmentDetails"
              defaultMessage="Show shipment details"
            />
          </StyledText>
        }
      >
        {content}
      </CustomCollapsibleSection>
    </div>
  );
};
